// Need to use the React-specific entry point to import createApi
import type {
  AssetMoveRequest,
  AssetPathResponse,
  AssetRequest,
  AssetResponse,
  GetAssetListRequest,
  GetAssetListRequestV2,
  GetAssetRelatedFormsListRequestV2,
  GetAssetTreeRequest,
  GetListResponseOfAssetRelatedFormSimpleResponse,
  GetListResponseOfAssetSimpleResponse,
  GetListResponseOfSelectOption,
  TreeResponse,
} from 'src/api/ProactApiClient';
import {assetApiClient} from 'src/api/ProactApiClientInstances';
import {
  assetPathQueryFn,
  assetQueryFn,
  assetTreeQueryFn,
} from 'src/app/forms/offline/reads';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Define a service using a base URL and expected endpoints
export const assetTypeApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getAssetSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetAssetListRequest
    >({
      queryFn: clientQuery(params => assetApiClient.listSelectOptions(params)),
      providesTags: ['Assets'],
    }),
    getAssetList: builder.query<
      GetListResponseOfAssetSimpleResponse,
      GetAssetListRequest
    >({
      queryFn: clientQuery(params => assetApiClient.list(params)),
      providesTags: ['Assets'],
    }),
    getAssetListV2: builder.query<
      GetListResponseOfAssetSimpleResponse,
      GetAssetListRequestV2
    >({
      queryFn: clientQuery(params => assetApiClient.listV2(params)),
      providesTags: ['Assets'],
    }),
    getAssetRelatedFormsListV2: builder.query<
      GetListResponseOfAssetRelatedFormSimpleResponse,
      GetAssetRelatedFormsListRequestV2
    >({
      queryFn: clientQuery(params =>
        assetApiClient.getRelatedFormsListV2(params)
      ),
      providesTags: ['Assets'],
    }),
    getAssetTree: builder.query<TreeResponse[], GetAssetTreeRequest>({
      queryFn: assetTreeQueryFn(params => assetApiClient.tree(params)),
      providesTags: ['Assets'],
    }),
    getAsset: builder.query<AssetResponse, number>({
      queryFn: assetQueryFn(id => assetApiClient.get(id)),
      providesTags: ['Assets'],
    }),
    getAssetPath: builder.query<AssetPathResponse, number>({
      queryFn: assetPathQueryFn(id => assetApiClient.getAssetPath(id)),
      providesTags: ['Assets'],
    }),
    createAsset: builder.mutation<AssetResponse, AssetRequest>({
      queryFn: clientQuery(request => assetApiClient.create(request)),
      invalidatesTags: ['Assets'],
    }),
    updateAsset: builder.mutation<AssetResponse, AssetRequest>({
      queryFn: clientQuery(request => assetApiClient.update(request)),
      invalidatesTags: ['Assets'],
    }),
    moveAsset: builder.mutation<AssetResponse, AssetMoveRequest>({
      queryFn: clientQuery(request => assetApiClient.move(request)),
      invalidatesTags: ['Assets'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAssetListQuery,
  useGetAssetSelectOptionsQuery,
  useGetAssetListV2Query,
  useGetAssetQuery,
  useGetAssetPathQuery,
  useGetAssetTreeQuery,
  useGetAssetRelatedFormsListV2Query,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useMoveAssetMutation,
} = assetTypeApi;
